import { memo, useState, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cpApi } from '../api/api';
import { VndButtonSpinner } from '../components/VndPageLoading';
import LanguageSwitcher from '../components/LanguageSwitcher';

const LOGO_SRC = '/images/wt-logo-512.png';
const LOGO_BG_SRC = '/images/wt-logo-256.png';

/** Вне Login — иначе при каждом onChange логотип размонтируется и «дёргается». */
const LoginLogo = memo(function LoginLogo({ size = 'lg' }: { size?: 'sm' | 'lg' }) {
  const box = size === 'lg' ? 'h-20 w-20 sm:h-24 sm:w-24' : 'h-16 w-16 sm:h-20 sm:w-20';
  return (
    <div
      className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/95 shadow-md mx-auto mb-4 ${box}`}
    >
      <img
        src={LOGO_SRC}
        alt="WT"
        className="h-full w-full object-contain p-1.5 drop-shadow-lg"
        decoding="async"
      />
    </div>
  );
});

function mapLoginError(msg: string, t: (k: string) => string): string {
  if (msg.includes('access denied: use platform')) {
    return t('login.errorPlatform');
  }
  if (msg.includes('access denied: use counterparty cabinet')) {
    return t('login.errorCabinet');
  }
  return msg;
}

const SAVED_LOGIN_KEY = 'airex_cp_saved_login';
const REMEMBER_ME_KEY = 'airex_cp_remember_me';

export default function Login() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ login: '', password: '' });
  const [code2FA, setCode2FA] = useState('');
  const [show2FA, setShow2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const saved = localStorage.getItem(SAVED_LOGIN_KEY);
    const remembered = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
    if (saved && remembered) {
      setFormData((prev) => ({ ...prev, login: saved }));
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (show2FA && code2FA.trim().length < 6) {
      setError(t('login.twoFactorCodeTooShort'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (show2FA && code2FA.trim()) {
        const res = await cpApi.loginWith2FA(formData.login, code2FA.trim(), formData.password);
        if (res.token && res.counterparty) {
          localStorage.setItem('airex_cp_token', res.token);
          if (rememberMe) {
            localStorage.setItem(SAVED_LOGIN_KEY, formData.login);
            localStorage.setItem(REMEMBER_ME_KEY, 'true');
          } else {
            localStorage.removeItem(SAVED_LOGIN_KEY);
            localStorage.removeItem(REMEMBER_ME_KEY);
          }
          navigate('/requests', { replace: true });
        }
      } else {
        const res = await cpApi.login({ login: formData.login, password: formData.password });
        if (res.requires_2fa) {
          setShow2FA(true);
        } else if (res.requires_2fa_setup && res.token) {
          localStorage.setItem('airex_cp_token', res.token);
          if (rememberMe) {
            localStorage.setItem(SAVED_LOGIN_KEY, formData.login);
            localStorage.setItem(REMEMBER_ME_KEY, 'true');
          } else {
            localStorage.removeItem(SAVED_LOGIN_KEY);
            localStorage.removeItem(REMEMBER_ME_KEY);
          }
          navigate('/settings', { replace: true });
        } else if (res.token && res.counterparty) {
          localStorage.setItem('airex_cp_token', res.token);
          if (rememberMe) {
            localStorage.setItem(SAVED_LOGIN_KEY, formData.login);
            localStorage.setItem(REMEMBER_ME_KEY, 'true');
          } else {
            localStorage.removeItem(SAVED_LOGIN_KEY);
            localStorage.removeItem(REMEMBER_ME_KEY);
          }
          navigate('/requests', { replace: true });
        }
      }
    } catch (err: unknown) {
      const res = (err as { response?: { data?: string | { error?: string } } })?.response?.data;
      const msg = typeof res === 'string' ? res : (res?.error ?? t('login.errorGeneric'));
      setError(mapLoginError(msg, t));
    } finally {
      setLoading(false);
    }
  };

  const handle2FABack = () => {
    setShow2FA(false);
    setCode2FA('');
    setError(null);
  };

  const errorBlock = error && (
    <div className="bg-red-500/20 border border-red-500/30 text-red-200 px-4 py-3 rounded-xl backdrop-blur-sm">
      {error}
    </div>
  );

  if (show2FA) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-sky-900 flex items-start justify-center pt-16 sm:pt-20 pb-12 px-4 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-[0.12] blur-3xl"
          style={{ backgroundImage: `url(${LOGO_BG_SRC})`, backgroundSize: '80%' }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/20 to-transparent" />

        <div className="max-w-md w-full relative z-10">
          <div className="text-center mb-6 sm:mb-8">
            <LoginLogo size="sm" />
            <div className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-300 mb-2">
              {t('login.brandSubtitle')}
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-white mb-1">{t('login.twoFactorTitle')}</h2>
            <p className="text-sm text-white/70">{t('login.twoFactorHint')}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl p-6 sm:p-8 border border-white/20">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="twofa_code" className="block text-sm font-medium text-white/90 mb-2">
                  {t('login.code2FA')}
                </label>
                <input
                  id="twofa_code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder={t('login.twoFactorCodePlaceholder')}
                  maxLength={16}
                  value={code2FA}
                  onChange={(e) =>
                    setCode2FA(e.target.value.replace(/[^0-9A-Za-z-]/g, '').slice(0, 16))
                  }
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-center text-xl tracking-[0.5em] font-mono"
                />
              </div>
              {errorBlock}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handle2FABack}
                  className="flex-1 py-3 px-4 border border-white/20 rounded-xl text-white bg-white/10 hover:bg-white/20 transition-all"
                >
                  {t('common.back')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 px-4 rounded-xl font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 focus:ring-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <VndButtonSpinner />
                      {t('common.loading')}
                    </span>
                  ) : (
                    t('login.submit')
                  )}
                </button>
              </div>
            </form>
          </div>
          <div className="mt-6 flex w-full justify-center px-1">
            <LanguageSwitcher variant="login" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-sky-900 flex items-start justify-center pt-16 sm:pt-20 pb-12 px-4 relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-[0.12] blur-3xl"
        style={{ backgroundImage: `url(${LOGO_BG_SRC})`, backgroundSize: '80%' }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/20 to-transparent" />

      <div className="max-w-md w-full relative z-10">
        <div className="text-center mb-6 sm:mb-8">
          <LoginLogo size="lg" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{t('login.welcome')}</h1>
          <div className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-300">
            {t('login.brandSubtitle')}
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl p-6 sm:p-8 border border-white/20">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="login" className="block text-sm font-medium text-white/90 mb-2">
                {t('login.loginLabel')}
              </label>
              <input
                id="login"
                name="login"
                type="text"
                required
                placeholder={t('login.loginPlaceholder')}
                value={formData.login}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/90 mb-2">
                {t('login.passwordLabel')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                placeholder={t('login.passwordPlaceholder')}
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
              />
            </div>
            <div className="flex items-center">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-indigo-500 border-white/30 rounded bg-white/10"
              />
              <label htmlFor="remember-me" className="ml-2 text-sm text-white/90">
                {t('login.rememberMe')}
              </label>
            </div>
            {errorBlock}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-xl font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 focus:ring-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <VndButtonSpinner />
                  {t('common.loading')}
                </span>
              ) : (
                t('login.submit')
              )}
            </button>
          </form>
        </div>
        <div className="mt-6 flex w-full justify-center px-1">
          <LanguageSwitcher variant="login" />
        </div>
      </div>
    </div>
  );
}
